// SPEC-029 M3: the standup in a Meet room. The farm owner's browser runs it as the head page does (M1):
// Pioneer hosts, each agent on the agenda checks in, Pioneer closes with the blockers. Every line is posted
// to the room, where Alpha voices it once and every viewer plays it on the speaker's head.
// ponytail: prompt, parsing and summary are copied from ~/pioneer-landing standup.js (minus Office posting).
import { request, type RoomState, type Session } from "./api";
import { HOST, Stage, agentVoice } from "./heads";

export type Note = { name: string; reply: string | null; error?: string };

export function prompt(day: string) {
	return "Pioneer daily standup, " + day + ". Reply with exactly three short lines and nothing else:\n" +
		"Done: what you finished since the last standup.\nDoing: what you are working on now.\n" +
		"Blockers: what is in your way, or none.\nBase it on your real recent work: your memory, workspace and logs. Under 70 words.";
}
// Agents write "Done:", "**Done:**" or "- Done:"; anything else in the reply is ignored.
export function parse(text: string) {
	const line = (key: string) => (text.match(new RegExp("^[\\s*_-]*" + key + "[*_]*\\s*:[*_]*\\s*(.+)$", "im")) || [])[1]?.trim() || "";
	return { done: line("Done"), doing: line("Doing"), blockers: line("Blockers?") };
}
export function summary(notes: Note[]) {
	const blocked = notes
		.filter((n) => n.reply)
		.map((n) => ({ name: n.name, text: parse(n.reply!).blockers.replace(/[.\s]+$/, "") }))
		.filter((b) => b.text && !/^(none|no blockers?|nothing)\b/i.test(b.text));
	const missed = notes.filter((n) => !n.reply).map((n) => n.name);
	return (blocked.length ? "Blockers: " + blocked.map((b) => b.name + ": " + b.text).join(". ") + "." : "No blockers today.") +
		missed.map((name) => " " + name + " did not check in.").join("");
}

type Agent = { slug: string; name: string };

/** Seats Pioneer plus the agenda in the room; `say` voices a line on a head, `ask` runs one agent turn. */
export async function openRoom(meeting: string, peerId: string, session: Session, stage: Stage, agenda: string[] | null, label: string) {
	const farm = await request<{ owner: boolean; agents: Agent[] }>("/api/v1/agents/farm", session);
	// Standups use the complete current roster, including agents whose runtime is
	// unavailable: their missed check-in must be visible rather than silently omitted.
	// Kickoffs keep the explicitly selected agenda.
	const agents = agenda === null ? farm.agents
		: agenda.map((slug) => farm.agents.find((a) => a.slug === slug)).filter((a): a is Agent => !!a);
	if (!agents.length) throw new Error(`None of the ${label} agents are in the farm.`);
	const post = (body: Record<string, unknown>) =>
		request<RoomState>(`/api/v1/meetings/${meeting}`, session, { ...body, peerId }, { timeout: 130_000 });
	const say = async (speaker: string, text: string, mood = "neutral") => {
		const state = await post({ type: "say", speaker, text: text.replace(/[*_#`]/g, ""), mood, voice: agentVoice(speaker) });
		stage.take(state.speech);
		await stage.played(state.speech.at(-1)!.id);
	};
	await post({ type: "agents", agents: [{ slug: HOST, name: "Pioneer" }, ...agents.map(({ slug, name }) => ({ slug, name }))] });
	// One agent at a time: the farm's agents share Beast's model, and a second turn started while the first
	// runs comes back 503 agent_unavailable. A per-meeting session keeps the meeting out of each agent's
	// other threads. ponytail: no client deadline beyond Alpha's fifteen minutes.
	const ask = (a: Agent, content: string) =>
		request<{ choices?: { message?: { content?: string } }[] }>(
			`/api/v1/agents/${a.slug}/messages`,
			session,
			{ content, session: `${label}-${meeting}` },
			{ timeout: 16 * 60_000, headers: { "X-Pioneer-Farm": "1" } },
		).then(
			(r): Note => ({ name: a.name, reply: r.choices?.[0]?.message?.content?.trim() || null, error: "an empty reply" }),
			(e): Note => ({ name: a.name, reply: null, error: e instanceof Error ? e.message : "failed" }),
		);
	return { agents, say, ask, ready: () => stage.ready([HOST, ...agents.map((a) => a.slug)], 30_000) };
}

export async function runStandup(meeting: string, peerId: string, session: Session, stage: Stage, status: (text: string) => void) {
	const { agents, say, ask, ready } = await openRoom(meeting, peerId, session, stage, null, "standup");
	status("Loading faces…");
	await ready();
	const day = new Date().toISOString().slice(0, 10);
	const names = agents.map((a) => a.name).join(" and ");
	status("Standup running");
	await say(HOST, `Good morning. This is standup. ${names}, one at a time: what you did, what you are doing, and what is in your way.`, "smile");
	const notes: Note[] = [];
	for (const a of agents) {
		await say(HOST, `${a.name}, you are up.`);
		status(`Waiting for ${a.name}…`);
		const note = await ask(a, prompt(day));
		notes.push(note);
		status("Standup running");
		if (note.reply) await say(a.slug, note.reply);
		else await say(HOST, `${a.name} did not check in: ${note.error!.replace(/[.\s]+$/, "")}. Moving on.`, "concerned");
	}
	await say(HOST, `That is standup. ${summary(notes)}`, "smile");
	status("Standup done");
}
